<template>
    <vx-expandable-card>
        <template v-slot:header>
            <span class="capitalize">{{ props.type }}</span>
        </template>
        <template v-slot:headerRight>
            <vx-badge :color="statusColor">
                {{ statusText }}
            </vx-badge>
        </template>
        <template v-slot:content>
            <div class="space-y-4">
                <div>
                    <vx-label :for="`${props.type}-url`">SFTP URL</vx-label>
                    <vx-input
                        :id="`${props.type}-url`"
                        v-model="formData.url"
                        type="text"
                        placeholder="sftp://example.com/path/to/file.csv"
                        :error="displayFirstError(validate.url.$errors)"
                        @blur="validate.url.$touch()"
                        data-test="url-input"
                    />
                </div>

                <div v-if="formData.url">
                    <vx-label :for="`${props.type}-username`">Username</vx-label>
                    <vx-input
                        :id="`${props.type}-username`"
                        v-model="formData.username"
                        type="text"
                        placeholder="Username"
                        :error="displayFirstError(validate.username.$errors)"
                        @blur="validate.username.$touch()"
                        data-test="username-input"
                    />
                </div>

                <div v-if="formData.url">
                    <vx-label :for="`${props.type}-password`">Password</vx-label>
                    <vx-input
                        :id="`${props.type}-password`"
                        v-model="formData.password"
                        :type="showPassword ? 'text' : 'password'"
                        :placeholder="
                            props.existing?.has_password && !isPasswordTouched ? '••••••••' : 'Enter password'
                        "
                        :toggle-password="isPasswordTouched"
                        :error="displayFirstError(validate.password.$errors)"
                        @blur="validate.password.$touch()"
                        @input="isPasswordTouched = true"
                        @toggle-password="showPassword = !showPassword"
                        data-test="password-input"
                    />
                    <vx-button
                        v-if="props.existing?.has_password && !isPasswordDeleted"
                        type="button"
                        color="danger"
                        size="sm"
                        class="mt-2"
                        @click="deletePassword"
                        data-test="delete-password-button"
                    >
                        Delete Password
                    </vx-button>
                </div>

                <div v-if="formData.url">
                    <vx-label :for="`${props.type}-ssh-key`">SSH Private Key</vx-label>
                    <vx-input
                        :id="`${props.type}-ssh-key`"
                        v-model="formData.ssh_private_key"
                        type="textarea"
                        :rows="6"
                        :placeholder="
                            props.existing?.has_ssh_private_key && !isSshKeyTouched
                                ? '••••••••'
                                : '-----BEGIN OPENSSH PRIVATE KEY-----\n...\n-----END OPENSSH PRIVATE KEY-----'
                        "
                        class="font-mono text-sm"
                        :error="displayFirstError(validate.ssh_private_key.$errors)"
                        @blur="validate.ssh_private_key.$touch()"
                        @input="isSshKeyTouched = true"
                        data-test="ssh-key-input"
                    />
                    <vx-button
                        v-if="props.existing?.has_ssh_private_key && !isSshKeyDeleted"
                        type="button"
                        color="danger"
                        size="sm"
                        class="mt-2"
                        @click="deleteSshKey"
                        data-test="delete-ssh-key-button"
                    >
                        Delete SSH Key
                    </vx-button>
                </div>

                <div v-if="formData.url" class="mt-6 flex items-center gap-4">
                    <vx-button
                        type="button"
                        :disabled="processing"
                        :processing="processing"
                        @click="handleSave"
                        data-test="save-button"
                    >
                        {{ props.existing?.url ? 'Update' : 'Save' }}
                    </vx-button>

                    <vx-button
                        v-if="props.existing?.url"
                        type="button"
                        color="danger"
                        :disabled="processing"
                        @click="handleDelete"
                        data-test="delete-button"
                    >
                        Delete
                    </vx-button>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { VxExpandableCard, VxLabel, VxInput, VxButton, VxBadge, VxSwal } from '@voxie/frontend-components';
import { useToasted } from '~/composables/useToasted';
import teamsClient from '~/services/teamsClient';
import type { FtpPullDataTypeConfig, FtpPullExistingConfig } from '~/types/ftp-pull.types';
import { catchRequestError } from '~/utils/request';
import { displayFirstError } from '~/utils/validation';

interface Props {
    type: 'contacts' | 'conversions' | 'groups';
    existing?: FtpPullExistingConfig;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    configUpdated: [];
}>();

const toasted = useToasted();

const statusColor = computed(() => {
    if (props.existing?.url) {
        return 'success';
    }
    return 'slate';
});

const statusText = computed(() => {
    if (props.existing?.url) {
        return 'Active';
    }
    return 'Not Active';
});

const formData = ref<FtpPullDataTypeConfig>({
    url: props.existing?.url || '',
    username: props.existing?.username || '',
    password: '',
    ssh_private_key: '',
});

const showPassword = ref(false);
const isPasswordTouched = ref(false);
const isSshKeyTouched = ref(false);
const processing = ref(false);
const isPasswordDeleted = ref(false);
const isSshKeyDeleted = ref(false);
const originalUrl = ref(props.existing?.url || '');

watch(
    () => props.existing,
    (newExisting) => {
        formData.value.url = newExisting?.url || '';
        formData.value.username = newExisting?.username || '';
        formData.value.password = '';
        formData.value.ssh_private_key = '';
        originalUrl.value = newExisting?.url || '';
        isPasswordTouched.value = false;
        isSshKeyTouched.value = false;
        // Only reset deletion flags if credentials actually exist in new config
        isPasswordDeleted.value = !newExisting?.has_password;
        isSshKeyDeleted.value = !newExisting?.has_ssh_private_key;
        showPassword.value = false;
        validate.value.$reset();
    },
    { deep: true }
);

watch(
    () => formData.value.password,
    (newValue) => {
        if (newValue && isPasswordDeleted.value) {
            isPasswordDeleted.value = false;
        }
    }
);

watch(
    () => formData.value.ssh_private_key,
    (newValue) => {
        if (newValue && isSshKeyDeleted.value) {
            isSshKeyDeleted.value = false;
        }
    }
);

const startsWithSftp = helpers.withMessage(
    'The URL must start with sftp://',
    (value: string) => !value || value.startsWith('sftp://')
);

const noEmbeddedCredentials = helpers.withMessage(
    'The URL cannot contain embedded credentials',
    (value: string) => !value || !value.match(/sftp:\/\/[^@]*:[^@]*@/)
);

const validUrlFormat = helpers.withMessage(
    'The URL must end with / for directories or with .csv or .zip for files',
    (value: string) => {
        if (!value) {
            return true;
        }

        // Extract path
        const pathMatch = value.match(/sftp:\/\/[^/]+(\/.*)?$/);
        const path = pathMatch?.[1] || '/';

        // Valid if it ends with / (directory) or .csv/.zip (file)
        if (path.endsWith('/')) {
            return true;
        }

        const filename = path.split('/').pop()?.toLowerCase() || '';
        return filename.endsWith('.csv') || filename.endsWith('.zip');
    }
);

const requireAuthMethod = helpers.withMessage('At least a password or SSH key is required.', () => {
    if (!formData.value.url) {
        return true;
    }
    const hasPassword = (formData.value.password || props.existing?.has_password) && !isPasswordDeleted.value;
    const hasSshKey = (formData.value.ssh_private_key || props.existing?.has_ssh_private_key) && !isSshKeyDeleted.value;
    return !!(hasPassword || hasSshKey);
});

const noPassphrasedOpenSshKey = helpers.withMessage(
    'Passphrase-protected OpenSSH keys are not supported due to performance issues. Please provide an unencrypted SSH key.',
    (value: string) => {
        if (!value?.includes('-----BEGIN OPENSSH PRIVATE KEY-----')) {
            return true;
        }

        try {
            const content = value.replace(
                /-----BEGIN OPENSSH PRIVATE KEY-----|-----END OPENSSH PRIVATE KEY-----|\s/g,
                ''
            );
            return !atob(content).includes('bcrypt');
        } catch {
            return true;
        }
    }
);

const rules = computed(() => ({
    url: { startsWithSftp, noEmbeddedCredentials, validUrlFormat },
    username: {
        required: helpers.withMessage('Username is required when a URL is provided.', (value: string) => {
            if (!formData.value.url) {
                return true;
            }

            return !!value;
        }),
    },
    password: { requireAuthMethod },
    ssh_private_key: { requireAuthMethod, noPassphrasedOpenSshKey },
}));

const validate = useVuelidate(rules, formData);

const handleSave = async () => {
    if (props.existing?.url && formData.value.url !== originalUrl.value) {
        const result = await VxSwal.fire({
            title: 'Confirm URL Change',
            text: 'Make sure to update the username, password, and/or SSH key.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, continue',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) {
            return;
        }
    }

    const isValid = await validate.value.$validate();
    if (!isValid) {
        return;
    }

    processing.value = true;

    try {
        const payload: Record<string, FtpPullDataTypeConfig> = {
            [props.type]: {
                url: formData.value.url,
                username: formData.value.username,
                password: isPasswordDeleted.value ? '__DELETE__' : formData.value.password,
                ssh_private_key: isSshKeyDeleted.value ? '__DELETE__' : formData.value.ssh_private_key,
            },
        };

        await teamsClient.saveFtpPullConfig(Spark.state.currentTeam.id, payload);
        toasted.success(
            `${props.type.charAt(0).toUpperCase() + props.type.slice(1)} configuration saved successfully.`
        );

        formData.value.password = '';
        formData.value.ssh_private_key = '';
        isPasswordTouched.value = false;
        isSshKeyTouched.value = false;
        originalUrl.value = formData.value.url;
        validate.value.$reset();

        // Notify parent to reload config (watcher will reset deletion flags based on new config)
        emit('configUpdated');
    } catch (error: unknown) {
        toasted.error(catchRequestError(error));
    } finally {
        processing.value = false;
    }
};

const handleDelete = async () => {
    const result = await VxSwal.fire({
        title: 'Delete Configuration',
        text: `Are you sure you want to delete the ${props.type} configuration?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it',
        cancelButtonText: 'Cancel',
        confirmButtonColor: 'danger',
    });

    if (!result.isConfirmed) {
        return;
    }

    processing.value = true;

    try {
        await teamsClient.deleteFtpPullConfig(Spark.state.currentTeam.id, props.type);
        toasted.success(
            `${props.type.charAt(0).toUpperCase() + props.type.slice(1)} configuration deleted successfully.`
        );

        emit('configUpdated');
    } catch (error) {
        toasted.error(catchRequestError(error));
    } finally {
        processing.value = false;
    }
};

const deletePassword = () => {
    isPasswordDeleted.value = true;
    isPasswordTouched.value = true;
    formData.value.password = '';
};

const deleteSshKey = () => {
    isSshKeyDeleted.value = true;
    isSshKeyTouched.value = true;
    formData.value.ssh_private_key = '';
};
</script>
